import { useCallback, useState } from 'react';

import type { AppSettings, FulfillmentModel, PartnerStoreSummary } from '../types/system.types';
import { systemApi } from '../api/system.api';
import { PRICING_SETTINGS_DEFAULTS } from '../../../../shared/pricingDefaults';

const SETTINGS_STORAGE_KEY = 'ninpo:settings';

const defaultSettings: AppSettings = {
  ...PRICING_SETTINGS_DEFAULTS,
  surgeMultiplier: 1,
  referralBonus: 5.0,
  maintenanceMode: false,
  maintenanceModeAllowReadOnly: false,
  allowGuestCheckout: true,
  checkoutAvailable: true,
  ownStoreInventoryEnabled: false,
  fulfillmentModel: 'passthrough_shopper' as FulfillmentModel,
  shoppingCloseBufferMinutes: 60,
  hubShoppingCloseBufferMinutes: 0,
  partnerStores: [] as PartnerStoreSummary[],
  allowGreenTier: false,
  receiptRequiredBeforeDelivery: true,
  ownerAutoAssignEnabled: true,
  geminiMaxOutputTokens: 4096,
  geminiDailyTokenCapPerActor: 200_000,
  geminiDailyCostCapUsdPerActor: 0,
  priceLockDays: 7,
  cooldownMs: 1000,
  beepEnabled: true,
  glassHandlingFeePerContainer: 0.02,
  returnHandlingFeePerContainer: 0.02,
  announcementBannerText: '',
  announcementBannerBadge: 'Announcement',
  defaultMarkupType: 'percent' as 'percent' | 'amount',
  defaultMarkupValue: 5,
};

const parseFiniteNumber = (value: unknown, fallback: number, options?: { min?: number; max?: number }) => {
  const parsed = Number(value ?? fallback);
  if (!Number.isFinite(parsed)) return fallback;

  if (typeof options?.min === 'number' && parsed < options.min) return options.min;
  if (typeof options?.max === 'number' && parsed > options.max) return options.max;

  return parsed;
};

/** Keys the server no longer returns; dropped from cached settings so stale localStorage cannot resurrect them. */
const RETIRED_SETTINGS_KEYS = [
  'pickupOnlyMultiplier', 'distanceBand1MaxMiles', 'distanceBand2MaxMiles', 'distanceBand1Rate', 'distanceBand2Rate',
  'distanceBand3Rate', 'serviceFee', 'freeDeliveryMaxMiles', 'workflowFlags', 'requirePhotoForRefunds',
  'showAdvancedInventoryInsights', 'allowReceiptApprovalCreateProduct', 'autoUpdateProductPriceFromReceipt', 'hubLat',
  'hubLng', 'storageZones', 'productTypes', 'scanningModesEnabled', 'defaultIncrement', 'requireSkuForScanning',
  'shelfGroupingEnabled', 'pricingEngineMode'
];

export const normalizeSettings = (raw?: Partial<AppSettings> | null): AppSettings => {
  const data = { ...(raw ?? {}) } as Partial<AppSettings> & Record<string, unknown>;
  for (const key of RETIRED_SETTINGS_KEYS) delete data[key];

  return {
    ...defaultSettings,
    ...data,
    routeFee: parseFiniteNumber(data.routeFee, defaultSettings.routeFee, { min: 0 }),
    surgeMultiplier: parseFiniteNumber(data.surgeMultiplier, defaultSettings.surgeMultiplier, { min: 0.5, max: 5 }),
    referralBonus: parseFiniteNumber(data.referralBonus, defaultSettings.referralBonus, { min: 0 }),
    distanceIncludedMiles: parseFiniteNumber(data.distanceIncludedMiles, defaultSettings.distanceIncludedMiles, { min: 0 }),
    maxDeliveryRadiusMiles: parseFiniteNumber(data.maxDeliveryRadiusMiles, defaultSettings.maxDeliveryRadiusMiles, { min: 0 }),
    maintenanceMode: Boolean(data.maintenanceMode ?? defaultSettings.maintenanceMode),
    maintenanceModeAllowReadOnly: Boolean(
      data.maintenanceModeAllowReadOnly ?? defaultSettings.maintenanceModeAllowReadOnly
    ),
    allowGuestCheckout: Boolean(data.allowGuestCheckout ?? defaultSettings.allowGuestCheckout),
    checkoutAvailable: Boolean(
      (data as Partial<AppSettings>).checkoutAvailable ?? defaultSettings.checkoutAvailable ?? true
    ),
    ownStoreInventoryEnabled: Boolean(data.ownStoreInventoryEnabled ?? defaultSettings.ownStoreInventoryEnabled),
    fulfillmentModel: (data.fulfillmentModel === 'hub_with_retail_backup'
      ? 'hub_with_retail_backup'
      : data.fulfillmentModel === 'passthrough_shopper'
        ? 'passthrough_shopper'
        : (data.ownStoreInventoryEnabled
          ? 'hub_with_retail_backup'
          : 'passthrough_shopper')) as FulfillmentModel,
    shoppingCloseBufferMinutes: parseFiniteNumber(
      data.shoppingCloseBufferMinutes,
      defaultSettings.shoppingCloseBufferMinutes ?? 60,
      { min: 0, max: 180 }
    ),
    hubShoppingCloseBufferMinutes: parseFiniteNumber(
      data.hubShoppingCloseBufferMinutes,
      defaultSettings.hubShoppingCloseBufferMinutes ?? 0,
      { min: 0, max: 180 }
    ),
    partnerStores: Array.isArray(data.partnerStores)
      ? data.partnerStores.map((row): PartnerStoreSummary => {
          const summary = row as PartnerStoreSummary;
          const openStatusRaw = String(summary.openStatus || '').trim();
          const openStatus: PartnerStoreSummary['openStatus'] =
            openStatusRaw === 'open' ||
            openStatusRaw === 'close_buffer' ||
            openStatusRaw === 'closed' ||
            openStatusRaw === 'unknown'
              ? openStatusRaw
              : undefined;
          return {
            storeId: String(summary.storeId || ''),
            storeName: String(summary.storeName || 'Store'),
            isDispatchHub: Boolean(summary.isDispatchHub),
            storeType: String(summary.storeType || 'other'),
            openStatus,
            isSelectableForImmediateShopping:
              typeof summary.isSelectableForImmediateShopping === 'boolean'
                ? summary.isSelectableForImmediateShopping
                : undefined
          };
        }).filter((row) => row.storeId)
      : defaultSettings.partnerStores,
    allowGreenTier: Boolean(data.allowGreenTier ?? defaultSettings.allowGreenTier),
    receiptRequiredBeforeDelivery: Boolean(
      data.receiptRequiredBeforeDelivery ?? defaultSettings.receiptRequiredBeforeDelivery
    ),
    ownerAutoAssignEnabled: Boolean(data.ownerAutoAssignEnabled ?? defaultSettings.ownerAutoAssignEnabled),
    geminiMaxOutputTokens: parseFiniteNumber(data.geminiMaxOutputTokens, defaultSettings.geminiMaxOutputTokens, {
      min: 256,
      max: 128_000
    }),
    geminiDailyTokenCapPerActor: parseFiniteNumber(
      data.geminiDailyTokenCapPerActor,
      defaultSettings.geminiDailyTokenCapPerActor,
      { min: 1000 }
    ),
    geminiDailyCostCapUsdPerActor: parseFiniteNumber(
      data.geminiDailyCostCapUsdPerActor,
      defaultSettings.geminiDailyCostCapUsdPerActor,
      { min: 0 }
    ),
    priceLockDays: parseFiniteNumber(data.priceLockDays, defaultSettings.priceLockDays, { min: 1 }),
    cooldownMs: parseFiniteNumber(data.cooldownMs, defaultSettings.cooldownMs, { min: 0 }),
    beepEnabled: Boolean(data.beepEnabled ?? defaultSettings.beepEnabled),
    glassHandlingFeePerContainer: parseFiniteNumber(data.glassHandlingFeePerContainer, defaultSettings.glassHandlingFeePerContainer, { min: 0 }),
    returnHandlingFeePerContainer: parseFiniteNumber(data.returnHandlingFeePerContainer, defaultSettings.returnHandlingFeePerContainer, { min: 0 }),
    largeOrderIncludedItems: parseFiniteNumber(data.largeOrderIncludedItems, defaultSettings.largeOrderIncludedItems, { min: 0 }),
    largeOrderPerItemFee: parseFiniteNumber(data.largeOrderPerItemFee, defaultSettings.largeOrderPerItemFee, { min: 0 }),
    largeOrderBracketItems: parseFiniteNumber(data.largeOrderBracketItems, defaultSettings.largeOrderBracketItems, { min: 1 }),
    heavyItemFeePerUnit: parseFiniteNumber(data.heavyItemFeePerUnit, defaultSettings.heavyItemFeePerUnit, { min: 0 }),
    heavyItemThresholdPoints: parseFiniteNumber(data.heavyItemThresholdPoints, defaultSettings.heavyItemThresholdPoints, { min: 0 }),
    extraStoreBaseFee: parseFiniteNumber(data.extraStoreBaseFee, defaultSettings.extraStoreBaseFee, { min: 0 }),
    smallOrderFee: parseFiniteNumber(data.smallOrderFee, defaultSettings.smallOrderFee, { min: 0 }),
    smallOrderThreshold: parseFiniteNumber(data.smallOrderThreshold, defaultSettings.smallOrderThreshold, { min: 0 }),
    smallOrderItemThreshold: parseFiniteNumber(data.smallOrderItemThreshold, defaultSettings.smallOrderItemThreshold, { min: 0 }),
    customerPerMileRate: parseFiniteNumber(data.customerPerMileRate, defaultSettings.customerPerMileRate, { min: 0 }),
    customerPerItemFee: parseFiniteNumber(data.customerPerItemFee, defaultSettings.customerPerItemFee, { min: 0 }),
    customRequestDeposit: parseFiniteNumber(data.customRequestDeposit, defaultSettings.customRequestDeposit, { min: 0 }),
    customRequestDepositBufferPercent: parseFiniteNumber(
      data.customRequestDepositBufferPercent,
      defaultSettings.customRequestDepositBufferPercent,
      { min: 0, max: 100 }
    ),
    announcementBannerText: String(data.announcementBannerText || defaultSettings.announcementBannerText || '').trim(),
    announcementBannerBadge: String(data.announcementBannerBadge || defaultSettings.announcementBannerBadge || '').trim(),
    defaultMarkupType: (data.defaultMarkupType === 'percent' || data.defaultMarkupType === 'amount')
      ? data.defaultMarkupType
      : defaultSettings.defaultMarkupType,
    defaultMarkupValue: parseFiniteNumber(data.defaultMarkupValue, defaultSettings.defaultMarkupValue, { min: 0 }),
  };
};

export const readStoredSettings = () => {
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(SETTINGS_STORAGE_KEY);
  if (!stored) return null;
  try {
    return normalizeSettings(JSON.parse(stored));
  } catch {
    return null;
  }
};

export const persistSettings = (next: AppSettings) => {
  if (typeof window === 'undefined') return false;
  try {
    window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(next));
    return true;
  } catch {
    return false;
  }
};

export const useSystemSettings = (
  addToast: (message: string, type?: 'info' | 'success' | 'warning' | 'error') => void,
  options?: { canSyncRemote?: boolean }
) => {
  const [settings, setSettings] = useState<AppSettings>(() => readStoredSettings() ?? normalizeSettings());
  const [isBackendOnline, setIsBackendOnline] = useState(true);
  const canSyncRemote = options?.canSyncRemote ?? false;

  const fetchSettings = useCallback(async () => {
    const stored = readStoredSettings();
    try {
      const storefront = await systemApi.storefront();
      const merged = normalizeSettings({
        ...(stored ?? {}),
        allowGuestCheckout: storefront.allowGuestCheckout,
        maintenanceMode: storefront.maintenanceMode,
        checkoutAvailable: storefront.checkoutAvailable,
        fulfillmentModel: storefront.fulfillmentModel,
        ownStoreInventoryEnabled: storefront.ownStoreInventoryEnabled,
        shoppingCloseBufferMinutes: storefront.shoppingCloseBufferMinutes,
        hubShoppingCloseBufferMinutes: storefront.hubShoppingCloseBufferMinutes,
        partnerStores: storefront.partnerStores
      });
      setSettings(merged);
      persistSettings(merged);
    } catch {
      if (stored) {
        setSettings(stored);
      } else {
        setSettings(normalizeSettings());
      }
    }

    if (!canSyncRemote) {
      return readStoredSettings() ?? normalizeSettings();
    }

    try {
      const data = await systemApi.settings();
      const payload = data?.settings ?? data;
      const normalized = normalizeSettings(payload as Partial<AppSettings>);
      setSettings(normalized);
      persistSettings(normalized);
      return normalized;
    } catch (e: unknown) {
      if (stored) {
        setSettings(stored);
        return stored;
      }
      const defaults = normalizeSettings();
      setSettings(defaults);
      const status = e && typeof e === 'object' && 'status' in e ? Number((e as { status?: number }).status) : 0;
      if (status !== 401 && status !== 403) {
        addToast(e instanceof Error ? e.message : 'Using default settings', 'warning');
      }
      return defaults;
    }
  }, [addToast, canSyncRemote]);

  const syncWithBackend = useCallback(async () => {
    try {
      await systemApi.healthz();
      setIsBackendOnline(true);
    } catch {
      setIsBackendOnline(false);
    }
  }, []);

  return { settings, setSettings, fetchSettings, syncWithBackend, isBackendOnline };
};
